/* VIVA audio pill player (snippets/viva-audio-player.liquid).
   idle → loading → playing (waveform progress + time) → paused / ended.
   - init on [data-viva-audio], idempotent (data-viva-audio-ready)
   - re-init on shopify:section:load
   - lazy src: <audio data-src> is copied to .src on first play (preload="none")
   - only one player plays at a time; pauses when scrolled out of view
   - load errors → data-state="error", label stays visible
   Each root exposes root.vivaAudio = { play, pause, toggle, audio }. */
(function () {
  'use strict';

  var BAR_COUNT = 48; // bars 29–48 hidden by CSS on mobile / sm size
  var players = [];

  // Deterministic pseudo-random bar heights (waveform look, identical every load).
  function heights(n) {
    var out = [];
    var seed = 7;
    for (var i = 0; i < n; i++) {
      seed = (seed * 9301 + 49297) % 233280;
      var r = seed / 233280;
      var envelope = 0.55 + 0.45 * Math.sin((i / n) * Math.PI * 2.6 + 0.4);
      out.push(Math.round((28 + r * 72) * envelope));
    }
    return out;
  }

  function fmt(sec) {
    sec = Math.max(0, Math.floor(isFinite(sec) ? sec || 0 : 0));
    var m = Math.floor(sec / 60);
    var s = sec % 60;
    return m + ':' + (s < 10 ? '0' : '') + s;
  }

  function init(root) {
    if (root.dataset.vivaAudioReady) return;
    root.dataset.vivaAudioReady = '1';

    var btn = root.querySelector('[data-viva-audio-toggle]');
    var wave = root.querySelector('[data-viva-audio-wave]');
    var time = root.querySelector('[data-viva-audio-time]');
    var audio = root.querySelector('[data-viva-audio-el]');
    var playIcon = root.querySelector('.viva-audio__icon-play');
    var pauseIcon = root.querySelector('.viva-audio__icon-pause');
    var loadingIcon = root.querySelector('.viva-audio__icon-loading');
    if (!btn || !wave || !audio) return;

    var bars = [];
    var frag = document.createDocumentFragment();
    heights(BAR_COUNT).forEach(function (h) {
      var i = document.createElement('i');
      i.style.setProperty('--h', h + '%');
      frag.appendChild(i);
      bars.push(i);
    });
    wave.appendChild(frag);

    var raf = 0;

    function setIconHidden(icon, hidden) {
      if (icon) icon.toggleAttribute('hidden', hidden);
    }

    function setState(s) {
      root.dataset.state = s;
      var active = s === 'loading' || s === 'playing';
      btn.setAttribute('aria-pressed', active ? 'true' : 'false');
      setIconHidden(playIcon, active);
      setIconHidden(pauseIcon, s !== 'playing');
      setIconHidden(loadingIcon, s !== 'loading');
    }

    function paint() {
      // Keep the "finished" look (all bars on, 0:00) until the user plays again;
      // the currentTime reset in `ended` fires a timeupdate that would clear it.
      if (root.dataset.state === 'ended') return;
      var d = audio.duration;
      var p = d && isFinite(d) ? audio.currentTime / d : 0;
      var on = Math.round(p * bars.length);
      for (var i = 0; i < bars.length; i++) {
        bars[i].classList.toggle('is-on', i < on);
      }
      var remaining = d && isFinite(d) ? Math.ceil(Math.max(0, d - audio.currentTime)) : 0;
      if (time) time.textContent = fmt(remaining);
    }

    function tick() {
      paint();
      if (!audio.paused && !audio.ended) raf = window.requestAnimationFrame(tick);
    }

    function stopTick() {
      if (raf) window.cancelAnimationFrame(raf);
      raf = 0;
    }

    function ensureSrc() {
      if (!audio.getAttribute('src') && audio.dataset.src) {
        audio.src = audio.dataset.src;
      }
      return !!audio.getAttribute('src');
    }

    function play() {
      if (!ensureSrc()) return;
      players.forEach(function (p) {
        if (p !== api) p.pause();
      });
      if (audio.readyState < 3) setState('loading');
      var pr = audio.play();
      if (pr && typeof pr.catch === 'function') {
        pr.catch(function () {
          if (root.dataset.state === 'loading') setState('idle');
        });
      }
    }

    function pause() {
      audio.pause();
      if (root.dataset.state === 'loading') setState('paused');
    }

    function toggle() {
      if (root.dataset.state === 'loading') pause();
      else if (audio.paused) play();
      else pause();
    }

    var api = { root: root, audio: audio, play: play, pause: pause, toggle: toggle };
    root.vivaAudio = api;
    players.push(api);
    setState('idle');

    btn.addEventListener('click', function () {
      if (btn.disabled) return;
      toggle();
    });

    audio.addEventListener('playing', function () {
      setState('playing');
      stopTick();
      tick();
    });
    audio.addEventListener('loadedmetadata', paint);
    audio.addEventListener('durationchange', paint);
    // Fallback for rAF throttling (background / hidden tabs): fires ~4×/s.
    audio.addEventListener('timeupdate', paint);
    audio.addEventListener('waiting', function () {
      if (!audio.paused) setState('loading');
    });
    audio.addEventListener('pause', function () {
      stopTick();
      paint();
      if (!audio.ended) setState('paused');
    });
    audio.addEventListener('ended', function () {
      stopTick();
      audio.currentTime = 0;
      bars.forEach(function (b) { b.classList.add('is-on'); });
      if (time) time.textContent = fmt(0);
      setState('ended');
    });
    audio.addEventListener('error', function () {
      stopTick();
      setState('error');
    });

    // Pause when scrolled out of view
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (!e.isIntersecting && !audio.paused) audio.pause();
        });
      }, { threshold: 0 }).observe(root);
    }
  }

  function boot() {
    document.querySelectorAll('[data-viva-audio]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  document.addEventListener('shopify:section:load', boot);
})();
